import * as React from 'react';
import { NavLink } from 'react-router-dom';
import { User, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { NavItem } from './types';
import { Profile } from '@/lib/types/core';
import { PremiumBadge } from '@/components/ui/premium-badge';
import { useAuth } from '@/contexts/AuthContext';

interface MobileNavigationProps {
  navItems: NavItem[];
  isOpen: boolean;
  profile: Profile | null;
  loading: boolean;
  handleProfileClick: () => void;
  handleSignOut: () => void;
  setShowAuthModal: (show: boolean) => void;
}

const MobileNavigation = ({
  navItems,
  isOpen,
  profile,
  loading,
  handleProfileClick,
  handleSignOut,
  setShowAuthModal,
}: MobileNavigationProps) => {
  const { isPremium } = useAuth();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-adv-dark/95 backdrop-blur-md flex flex-col items-center justify-center md:hidden animate-fade-in">
      <nav className="flex flex-col items-center space-y-6 p-8">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              `text-xl font-medium transition-colors hover:text-adv-orange ${
                isActive ? 'text-adv-orange' : 'text-white'
              }`
            }
          >
            {item.label}
          </NavLink>
        ))}
        <div className="flex flex-col space-y-4 pt-6 w-full">
          <NavLink to="/explore-rides" className="btn-primary w-full text-center">
            Find a Ride
          </NavLink>
          <NavLink to="/rallies" className="btn-primary w-full text-center">
            Join a Rally
          </NavLink>
          
          {/* User profile or sign in button for mobile */}
          {loading ? (
            <Button disabled variant="outline" className="w-full bg-adv-darkGray text-white flex justify-center items-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Loading...
            </Button>
          ) : profile ? (
            <>
              <Button
                onClick={handleProfileClick}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-md bg-adv-darkGray hover:bg-adv-charcoal text-white"
              >
                <User size={18} />
                <span className="flex items-center gap-1">
                  My Profile
                  {isPremium && <PremiumBadge size="sm" showTooltip={false} />}
                </span>
              </Button>
              <Button 
                onClick={handleSignOut}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white"
              >
                <LogOut size={18} />
                <span>Sign Out</span>
              </Button>
            </>
          ) : (
            <Button 
              onClick={() => setShowAuthModal(true)}
              className="w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-md bg-adv-darkGray hover:bg-adv-charcoal text-white"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-log-in"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" x2="3" y1="12" y2="12"/></svg>
              <span>Sign In</span>
            </Button>
          )}
        </div>
      </nav>
    </div>
  );
};

export default MobileNavigation;
